/**
 * Debug Log Ring Buffer
 * 
 * Captures console.* calls in a circular buffer for bug reporting.
 * Call installConsoleInterceptor() at app startup.
 */

export type LogLevel = 'log' | 'warn' | 'error' | 'info' | 'debug';

export interface DebugLogEntry {
    ts: number;
    level: LogLevel;
    args: unknown[];
}

const MAX_ENTRIES = 2000;
const buffer: DebugLogEntry[] = [];
let isInstalled = false;

// OUT_TRACE Gating: Only log [OUT_TRACE] if VITE_DEBUG_OUT=1
// Use deduplication to prevent same-frame spam even when enabled
const OUT_TRACE_ENABLED = import.meta.env.VITE_DEBUG_OUT === '1';
const seenTraces = new Set<string>();

/**
 * Install console interceptor to capture all console.* calls.
 * Safe to call multiple times (no-op if already installed).
 */
export function installConsoleInterceptor(): void {
    if (isInstalled) return;
    isInstalled = true;

    const levels: LogLevel[] = ['log', 'warn', 'error', 'info', 'debug'];

    for (const level of levels) {
        const original = console[level].bind(console);
        console[level] = (...args: unknown[]) => {
            const firstArg = args[0];
            const isOutTrace = typeof firstArg === 'string' && firstArg.startsWith('[OUT_TRACE]');

            if (isOutTrace) {
                // Return early if traces are globally disabled
                if (!OUT_TRACE_ENABLED) return;

                // Deduplicate to prevent flooding even when enabled
                const traceKey = args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join('|');
                if (seenTraces.has(traceKey)) return;
                seenTraces.add(traceKey);

                // Cap seenTraces size to prevent memory leak in long sessions
                if (seenTraces.size > 500) {
                    const first = seenTraces.values().next().value;
                    if (first !== undefined) seenTraces.delete(first);
                }
            }

            try {
                buffer.push({ ts: Date.now(), level, args });
                // Keep buffer size under MAX_ENTRIES
                if (buffer.length > MAX_ENTRIES) {
                    buffer.splice(0, buffer.length - MAX_ENTRIES);
                }
            } catch {
                // Silently fail - don't break console
            }
            original(...args);
        };
    }

    console.log('[DebugLogBuffer] Console interceptor installed');
}

/**
 * Get recent log entries.
 * @param limit Maximum number of entries to return (default 800)
 */
export function getLogs(limit = 800): DebugLogEntry[] {
    return buffer.slice(Math.max(0, buffer.length - limit));
}

/**
 * Clear all captured logs.
 */
export function clearLogs(): void {
    buffer.length = 0;
}

/**
 * Get buffer stats for debugging.
 */
export function getBufferStats(): { size: number; maxSize: number } {
    return { size: buffer.length, maxSize: MAX_ENTRIES };
}

// ============================================================================
// Sanitization
// ============================================================================

/**
 * Sanitize text to remove sensitive information before sending to server.
 */
export function sanitizeText(s: string): string {
    return s
        // Redact Alchemy API keys
        .replace(/alchemy\.com\/v2\/[A-Za-z0-9_-]+/g, 'alchemy.com/v2/<REDACTED>')
        // Redact Infura keys
        .replace(/infura\.io\/v3\/[A-Za-z0-9]+/g, 'infura.io/v3/<REDACTED>')
        // Redact Bearer tokens
        .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer <REDACTED>')
        // Redact Supabase keys (anon/service)
        .replace(/eyJ[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}/g, '<JWT_REDACTED>')
        // Redact private keys (0x + 64 hex chars)
        .replace(/0x[a-fA-F0-9]{64}/g, '<PRIVATE_KEY_REDACTED>');
}

/**
 * Sanitize log entries for transmission.
 */
export function sanitizeLogs(logs: DebugLogEntry[]): DebugLogEntry[] {
    return logs.map(entry => ({
        ...entry,
        args: entry.args.map(arg => {
            if (typeof arg === 'string') {
                return sanitizeText(arg);
            }
            if (typeof arg === 'object' && arg !== null) {
                try {
                    // Serialize and sanitize, then parse back
                    const serialized = JSON.stringify(arg);
                    return JSON.parse(sanitizeText(serialized));
                } catch {
                    return '[Unserializable Object]';
                }
            }
            return arg;
        }),
    }));
}

/**
 * Format logs for markdown display.
 */
export function formatLogsForMarkdown(logs: DebugLogEntry[], maxLines = 100): string {
    const recent = logs.slice(-maxLines);
    return recent.map(entry => {
        const time = new Date(entry.ts).toISOString().slice(11, 23); // HH:MM:SS.mmm
        const level = entry.level.toUpperCase().padEnd(5);
        const argsStr = entry.args.map(a => {
            if (typeof a === 'string') return a;
            try {
                return JSON.stringify(a);
            } catch {
                return String(a);
            }
        }).join(' ');
        return `[${time}] ${level} ${argsStr}`;
    }).join('\n');
}
